import React, { useState } from "react";
import {
  getMissingResizedImages,
  processSingleResizedImage,
  MissingImage,
} from "../../api/generateMissingImages";
import { handleError } from "../../utils/handleError";
import toast from "react-hot-toast";

interface ProcessingProgress {
  totalImages: number;
  processedImages: number;
  currentBatch: number;
  totalBatches: number;
  errors: string[];
  processed: {
    thumbnail: number;
    "960px": number;
    "2400px": number;
    "3600px": number;
  };
}

export function GenerateMissingImages() {
  const [isGettingList, setIsGettingList] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [missingImages, setMissingImages] = useState<MissingImage[]>([]);
  const [processingProgress, setProcessingProgress] =
    useState<ProcessingProgress | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [refreshHdr, setRefreshHdr] = useState(false);

  const handleGetMissingImages = async () => {
    setIsGettingList(true);
    setMissingImages([]);
    setProcessingProgress(null);
    setShowResults(false);

    try {
      const result = await getMissingResizedImages(refreshHdr);
      setMissingImages(result.missingImages);
      setShowResults(true);

      if (result.total === 0) {
        toast.success("All image derivatives are current");
      } else {
        toast.success(`Found ${result.total} images requiring processing`);
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsGettingList(false);
    }
  };

  const handleProcessImages = async () => {
    if (missingImages.length === 0) return;

    setIsProcessing(true);
    const batchSize = 3;
    const totalBatches = Math.ceil(missingImages.length / batchSize);

    const progress: ProcessingProgress = {
      totalImages: missingImages.length,
      processedImages: 0,
      currentBatch: 0,
      totalBatches,
      errors: [],
      processed: { thumbnail: 0, "960px": 0, "2400px": 0, "3600px": 0 },
    };

    setProcessingProgress(progress);

    try {
      for (let i = 0; i < missingImages.length; i += batchSize) {
        const batch = missingImages.slice(i, i + batchSize);
        progress.currentBatch = Math.floor(i / batchSize) + 1;
        setProcessingProgress({ ...progress });

        // Process batch in parallel
        const batchPromises = batch.map(async (missingImage) => {
          try {
            const result = await processSingleResizedImage(
              missingImage.name,
              missingImage.missingSizes
            );

            // Update progress counters
            if (result.processed.thumbnail) progress.processed.thumbnail++;
            if (result.processed["960px"]) progress.processed["960px"]++;
            if (result.processed["2400px"]) progress.processed["2400px"]++;
            if (result.processed["3600px"]) progress.processed["3600px"]++;

            progress.errors.push(...result.errors);
            progress.processedImages++;

            return result;
          } catch (error) {
            progress.errors.push(
              `Failed to process ${missingImage.name}: ${
                (error as Error).message
              }`
            );
            progress.processedImages++;
            throw error;
          }
        });

        // Wait for all images in the batch to complete
        await Promise.allSettled(batchPromises);

        // Update progress after each batch
        setProcessingProgress({ ...progress });
      }

      const totalProcessed =
        progress.processed.thumbnail +
        progress.processed["960px"] +
        progress.processed["2400px"] +
        progress.processed["3600px"];

      if (totalProcessed > 0) {
        toast.success(
          `Successfully generated ${totalProcessed} missing resized images across all sizes`
        );
      } else {
        toast.error("No images were processed successfully");
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="card bg-base-100 shadow-sm mb-12">
      <div className="card-body">
        <h3 className="card-title">Generate Image Derivatives</h3>
        <p className="text-sm text-base-content/70">
          Generate current thumbnail, 960px, 2400px, and 3600px versions. HDR
          sources retain HDR in every size except the thumbnail.
        </p>

        <label className="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            className="toggle toggle-primary"
            checked={refreshHdr}
            onChange={(event) => setRefreshHdr(event.target.checked)}
            disabled={isGettingList || isProcessing}
          />
          <span className="label-text">
            Refresh HDR images that already use the current pipeline
          </span>
        </label>

        <div className="card-actions mt-2">
          <button
            className={`btn btn-primary`}
            onClick={handleGetMissingImages}
            disabled={isGettingList || isProcessing}
          >
            {isGettingList ? (
              <span className="loading loading-spinner"></span>
            ) : null}
            {isGettingList
              ? "Getting List..."
              : "Step 1 - Check Image Derivatives"}
          </button>
        </div>

        {showResults && missingImages.length > 0 && (
          <div className="my-4">
            <div className="alert alert-info">
              <div>
                <div className="font-semibold">
                  Found {missingImages.length} images requiring processing
                </div>
                <div className="text-sm mt-2">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <span className="font-medium">Thumbnail missing:</span>{" "}
                      {
                        missingImages.filter((img) =>
                          img.missingSizes.includes("thumbnail")
                        ).length
                      }
                    </div>
                    <div>
                      <span className="font-medium">960px missing:</span>{" "}
                      {
                        missingImages.filter((img) =>
                          img.missingSizes.includes("960px")
                        ).length
                      }
                    </div>
                    <div>
                      <span className="font-medium">2400px missing:</span>{" "}
                      {
                        missingImages.filter((img) =>
                          img.missingSizes.includes("2400px")
                        ).length
                      }
                    </div>
                    <div>
                      <span className="font-medium">3600px missing:</span>{" "}
                      {
                        missingImages.filter((img) =>
                          img.missingSizes.includes("3600px")
                        ).length
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-actions mt-4">
              <button
                className={`btn btn-secondary`}
                onClick={handleProcessImages}
                disabled={isProcessing || missingImages.length === 0}
              >
                {isProcessing ? (
                  <span className="loading loading-spinner"></span>
                ) : null}
                {isProcessing
                  ? "Processing..."
                  : "Step 2 - Generate Image Derivatives"}
              </button>
            </div>
          </div>
        )}

        {showResults && missingImages.length === 0 && (
          <div className="my-4">
            <div className="alert alert-success">
              <div className="font-semibold">
                All image derivatives are current
              </div>
            </div>
          </div>
        )}

        {/* Processing Progress */}
        {processingProgress && (
          <div className="my-4">
            <div className="alert alert-info">
              <div>
                <div className="font-semibold">Processing Progress</div>
                <div className="text-sm mt-2">
                  <div className="mb-2">
                    Batch {processingProgress.currentBatch} of{" "}
                    {processingProgress.totalBatches}
                  </div>
                  <div className="mb-2">
                    Images: {processingProgress.processedImages} /{" "}
                    {processingProgress.totalImages}
                  </div>
                  <progress
                    className="progress progress-primary w-full"
                    value={processingProgress.processedImages}
                    max={processingProgress.totalImages}
                  ></progress>

                  {/* Results so far */}
                  <div className="grid grid-cols-4 gap-4 mt-2">
                    <div>
                      <span className="font-medium">Thumbnail:</span>{" "}
                      {processingProgress.processed.thumbnail}
                    </div>
                    <div>
                      <span className="font-medium">960px:</span>{" "}
                      {processingProgress.processed["960px"]}
                    </div>
                    <div>
                      <span className="font-medium">2400px:</span>{" "}
                      {processingProgress.processed["2400px"]}
                    </div>
                    <div>
                      <span className="font-medium">3600px:</span>{" "}
                      {processingProgress.processed["3600px"]}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Show errors if any */}
            {processingProgress.errors.length > 0 && (
              <div className="alert alert-warning mt-2">
                <div>
                  <div className="font-semibold">
                    Errors ({processingProgress.errors.length}):
                  </div>
                  <ul className="text-sm list-disc list-inside">
                    {processingProgress.errors
                      .slice(0, 5)
                      .map((error: string, index: number) => (
                        <li key={index}>{error}</li>
                      ))}
                    {processingProgress.errors.length > 5 && (
                      <li>
                        ... and {processingProgress.errors.length - 5} more
                        errors
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
